import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Typography, Stack, Button, Dialog, DialogContent, DialogActions,
    LinearProgress, Chip, IconButton, Tooltip, CircularProgress,
} from '@mui/material';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
// import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded'; // used by the disabled "Setup in progress" popup
// import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded'; // used by the disabled "Setup in progress" popup
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import RuleRoundedIcon from '@mui/icons-material/RuleRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import RequestQuoteRoundedIcon from '@mui/icons-material/RequestQuoteRounded';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectUserTypeId, USER_TYPE, selectEffectiveModules } from '../redux/slices/authSlice';
import { hasModule } from '../data/serverModules';
import { selectActiveEntityId } from '../redux/slices/orgSlice';
import http from '../Api/http';
import { GetEntitySetupStatus } from '../Api/Api';

const PRIMARY = '#7C5CFC';
const PRIMARY_LIGHT = '#F1EEFE';
const GRADIENT = 'linear-gradient(135deg, #7C5CFC 0%, #9B87FB 100%)';

// Label and destination live together, so a step can never be named one thing
// and open another. The API's own title is only used for steps we don't know.
const STEP_META = {
    financialYear: { icon: CalendarMonthRoundedIcon, label: 'Financial Year', to: '/dashboard/settings?tab=financial-year' },
    leaveTypes: { icon: CategoryRoundedIcon, label: 'Leave Types', to: '/dashboard/leave-policy?tab=types' },
    leavePolicy: { icon: RuleRoundedIcon, label: 'Leave Policy', to: '/dashboard/leave-policy?tab=setup' },
    shifts: { icon: ScheduleRoundedIcon, label: 'Shifts', to: '/dashboard/leave-policy?tab=setup' },
    workingCalendar: { icon: EventAvailableRoundedIcon, label: 'Working Calendar', to: '/dashboard/leave-policy?tab=calendar' },
    shiftAssignment: { icon: GroupsRoundedIcon, label: 'Assign Shifts', to: '/dashboard/leave-policy?tab=shifts' },
    entityAdmin: { icon: AdminPanelSettingsRoundedIcon, label: 'Entity Admin', to: `/dashboard/roles/${USER_TYPE.ADMIN}/users` },
    complianceConfig: { icon: VerifiedUserRoundedIcon, label: 'Statutory Configuration', to: '/dashboard/payroll-setup?tab=statutory' },
    salaryStructures: { icon: RequestQuoteRoundedIcon, label: 'Salary Structure', to: '/dashboard/payroll-setup?tab=structures' },
};
const FALLBACK = { icon: RadioButtonUncheckedRoundedIcon, to: null };

const ADMIN_SETUP_STEPS = new Set(['shiftAssignment', 'salaryStructures', 'complianceConfig']);

const SETUP_MODULES = ['assign-shifts', 'salary-structures', 'statutory-deductions'];

const FORCE_REQUIRED = ADMIN_SETUP_STEPS;

const normalizeSetup = (data) => {
    if (!data) return data;
    const steps = Array.isArray(data.steps)
        ? data.steps.map((s) => (FORCE_REQUIRED.has(s.key) ? { ...s, required: true } : s))
        : [];
    const pendingRequired = steps.filter((s) => s.required && !s.done).length;
    return { ...data, steps, pendingRequired, ready: pendingRequired === 0 };
};

const FAB = 50;
const FAB_KEY = 'setup-fab-pos';
const clampPos = (x, y) => ({
    x: Math.min(Math.max(8, x), Math.max(8, window.innerWidth - FAB - 8)),
    y: Math.min(Math.max(8, y), Math.max(8, window.innerHeight - FAB - 8)),
});
const loadPos = () => {
    try {
        const v = JSON.parse(localStorage.getItem(FAB_KEY) || 'null');
        if (v && Number.isFinite(v.x) && Number.isFinite(v.y)) return clampPos(v.x, v.y);
    } catch { /* default position */ }
    return clampPos(window.innerWidth - FAB - 20, window.innerHeight - FAB - 20);
};

// The floating "Finish setup" button. Drag it anywhere (the spot is remembered); a plain click opens
// the checklist. The wrapper keeps a fixed 50px size and only the inner pill widens on hover, toward
// the side with more room, so the hover target never jumps under the cursor.
function FloatingSetupButton({ pendingRequired, onOpen }) {
    const [pos, setPos] = useState(loadPos);
    const [dragging, setDragging] = useState(false);
    const drag = useRef(null);

    useEffect(() => {
        const onResize = () => setPos((p) => clampPos(p.x, p.y));
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const onDown = (e) => {
        if (e.button !== undefined && e.button !== 0) return;
        drag.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y, moved: false };
        e.currentTarget.setPointerCapture?.(e.pointerId);
    };
    const onMove = (e) => {
        const d = drag.current;
        if (!d) return;
        const dx = e.clientX - d.sx; const dy = e.clientY - d.sy;
        if (!d.moved && Math.hypot(dx, dy) < 5) return;
        if (!d.moved) { d.moved = true; setDragging(true); }
        setPos(clampPos(d.ox + dx, d.oy + dy));
    };
    const onUp = (e) => {
        const d = drag.current;
        drag.current = null;
        e.currentTarget.releasePointerCapture?.(e.pointerId);
        if (!d) return;
        if (d.moved) {
            setDragging(false);
            setPos((p) => { try { localStorage.setItem(FAB_KEY, JSON.stringify(p)); } catch { /* not saved */ } return p; });
        } else onOpen();
    };

    const openLeft = pos.x > window.innerWidth / 2;
    return (
        <Box
            role="button" tabIndex={0} aria-label={`Finish setup, ${pendingRequired} left. Drag to move.`}
            onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
            sx={{
                position: 'fixed', left: pos.x, top: pos.y, width: FAB, height: FAB, zIndex: 1400,
                touchAction: 'none', userSelect: 'none', cursor: dragging ? 'grabbing' : 'grab', outline: 'none',
                '& .setup-pill': {
                    position: 'absolute', top: 0, [openLeft ? 'right' : 'left']: 0, height: FAB, width: FAB,
                    display: 'flex', alignItems: 'center', justifyContent: 'flex-start', flexDirection: openLeft ? 'row-reverse' : 'row',
                    background: GRADIENT, color: '#fff', borderRadius: '999px', overflow: 'hidden', whiteSpace: 'nowrap',
                    fontWeight: 800, fontSize: 13,
                    transition: 'width .28s cubic-bezier(.22,.8,.3,1), box-shadow .2s ease',
                    willChange: 'width',
                    animation: dragging ? 'none' : 'setupGlow 2.4s ease-in-out infinite',
                    boxShadow: dragging ? '0 14px 30px -8px rgba(124,92,252,0.9)' : undefined,
                },
                '@keyframes setupGlow': {
                    '0%, 100%': { boxShadow: '0 0 0 0 rgba(124,92,252,0.45), 0 8px 20px -8px rgba(124,92,252,0.8)' },
                    '50%': { boxShadow: '0 0 0 8px rgba(124,92,252,0), 0 8px 24px -6px rgba(167,139,250,0.9)' },
                },
                '& .setup-label': { opacity: 0, transition: 'opacity .18s ease', pointerEvents: 'none', [openLeft ? 'pl' : 'pr']: 2 },
                '&:hover .setup-pill, &:focus-visible .setup-pill': { width: 210 },
                '&:hover .setup-label, &:focus-visible .setup-label': { opacity: 1, transitionDelay: '.1s' },
                '&:active .setup-pill': { filter: 'brightness(0.96)' },
            }}
        >
            <Box className="setup-pill">
                <Box sx={{ width: FAB, height: FAB, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <RocketLaunchRoundedIcon sx={{ fontSize: 22 }} />
                </Box>
                <Box component="span" className="setup-label">Finish setup · {pendingRequired} left</Box>
            </Box>
        </Box>
    );
}


export default function EntitySetupGate() {
    const navigate = useNavigate();
    const location = useLocation();
    // const dispatch = useDispatch(); // used by the disabled "Setup in progress" popup (Sign out)
    const userTypeId = useSelector(selectUserTypeId);
    const activeEntityId = useSelector(selectActiveEntityId);

    const isMasterAdmin = userTypeId === USER_TYPE.MASTER_ADMIN;
    const isAdmin = userTypeId === USER_TYPE.ADMIN;
    const modules = useSelector(selectEffectiveModules);
    const canConfigureSetup = isMasterAdmin || isAdmin || SETUP_MODULES.some((m) => hasModule(modules, m));

    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [autoShown, setAutoShown] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: body } = await http.get(GetEntitySetupStatus);
            setStatus(body && !body.error ? normalizeSetup(body.data || null) : null);
        } catch {
            setStatus(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { setAutoShown(false); load(); }, [activeEntityId, load]);

    useEffect(() => {
        if (status && !status.ready) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    useEffect(() => {
        if (status && !status.ready && !autoShown) { setOpen(true); setAutoShown(true); }
    }, [status, autoShown]);

    if (!status || status.ready) return null;

    const steps = Array.isArray(status.steps) ? status.steps : [];
    const pendingRequired = Number(status.pendingRequired) || 0;
    const completed = Number(status.completedSteps) || 0;
    const total = Number(status.totalSteps) || steps.length || 1;
    const pct = Math.round((completed / total) * 100);
    const entityName = status.entityName || 'this entity';

    // The "Setup in progress" popup that blocked employee / non-admin logins is DISABLED for now.
    // To turn it back on: delete the `if (!canConfigureSetup) return null;` line below, uncomment the block under it,
    // and uncomment the HourglassEmptyRoundedIcon / LogoutRoundedIcon imports, `useDispatch` and `logout`, and `dispatch`.
    if (!canConfigureSetup) return null;

    // const corePending = steps.some((s) => s.required && !s.done && !ADMIN_SETUP_STEPS.has(s.key));
    //
    // if (!canConfigureSetup) {
    //     if (!corePending) return null;
    //     return (
    //         <Box sx={{ position: 'fixed', inset: 0, zIndex: 2000, bgcolor: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
    //             <Box sx={{ width: '100%', maxWidth: 460, bgcolor: '#fff', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 30px 60px -20px rgba(15,23,42,0.5)' }}>
    //                 <Box sx={{ background: GRADIENT, p: 3, color: '#fff', textAlign: 'center' }}>
    //                     <HourglassEmptyRoundedIcon sx={{ fontSize: 40 }} />
    //                     <Typography sx={{ fontSize: 19, fontWeight: 800, mt: 1 }}>Setup in progress</Typography>
    //                 </Box>
    //                 <Box sx={{ p: 3, textAlign: 'center' }}>
    //                     <Typography sx={{ fontSize: 14, color: '#334155', lineHeight: 1.6 }}>
    //                         <strong>{entityName}</strong> isn&apos;t quite ready yet — your administrator is still finishing its setup.
    //                         You&apos;ll be able to sign in and use the app once it&apos;s done.
    //                     </Typography>
    //                     <Button
    //                         onClick={() => dispatch(logout())}
    //                         startIcon={<LogoutRoundedIcon sx={{ fontSize: 18 }} />}
    //                         sx={{ mt: 3, height: 44, px: 3, bgcolor: PRIMARY, color: '#fff', fontWeight: 700, borderRadius: '9px', textTransform: 'none', boxShadow: 'none', '&:hover': { bgcolor: '#6246E0' } }}
    //                     >
    //                         Sign out
    //                     </Button>
    //                 </Box>
    //             </Box>
    //         </Box>
    //     );
    // }

    const go = (to) => { if (to) { navigate(to); setOpen(false); } };

    return (
        <>
            {!open && <FloatingSetupButton pendingRequired={pendingRequired} onOpen={() => { load(); setOpen(true); }} />}

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                maxWidth="sm"
                fullWidth
                slotProps={{ paper: { sx: { borderRadius: '16px', overflow: 'hidden' } } }}
            >
                <Box sx={{ background: GRADIENT, p: 2.6, color: '#fff', position: 'relative' }}>
                    <IconButton onClick={() => setOpen(false)} size="small" sx={{ position: 'absolute', top: 10, right: 10, color: 'rgba(255,255,255,0.85)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.14)' } }}>
                        <CloseRoundedIcon sx={{ fontSize: 19 }} />
                    </IconButton>
                    <Stack direction="row" spacing={1.4} sx={{ alignItems: 'center' }}>
                        <Box sx={{ width: 42, height: 42, borderRadius: '11px', bgcolor: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <RocketLaunchRoundedIcon sx={{ fontSize: 23 }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontSize: 17, fontWeight: 800 }}>Set up {entityName}</Typography>
                            <Typography sx={{ fontSize: 12.5, opacity: 0.9 }}>
                                {pendingRequired > 0 ? `${pendingRequired} required step${pendingRequired === 1 ? '' : 's'} left before it can run` : 'Almost there — finish the optional steps'}
                            </Typography>
                        </Box>
                    </Stack>
                    <Box sx={{ mt: 1.8 }}>
                        <Stack direction="row" sx={{ justifyContent: 'space-between', mb: 0.6 }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, opacity: 0.9 }}>{completed} of {total} done</Typography>
                            <Typography sx={{ fontSize: 11, fontWeight: 800 }}>{pct}%</Typography>
                        </Stack>
                        <LinearProgress variant="determinate" value={pct} sx={{ height: 7, borderRadius: 5, bgcolor: 'rgba(255,255,255,0.25)', '& .MuiLinearProgress-bar': { bgcolor: '#fff', borderRadius: 5 } }} />
                    </Box>
                </Box>

                <DialogContent sx={{ p: 1.5 }}>
                    <Stack spacing={0.8}>
                        {steps.map((s) => {
                            const meta = STEP_META[s.key] || FALLBACK;
                            const Icon = s.done ? CheckCircleRoundedIcon : meta.icon;
                            return (
                                <Stack
                                    key={s.key}
                                    direction="row"
                                    spacing={1.4}
                                    sx={{
                                        alignItems: 'center', p: 1.3, borderRadius: '11px',
                                        border: '1px solid', borderColor: s.done ? '#DCFCE7' : '#EEF1F6',
                                        bgcolor: s.done ? '#F5FDF8' : '#fff',
                                    }}
                                >
                                    <Box sx={{ width: 38, height: 38, borderRadius: '10px', flexShrink: 0, bgcolor: s.done ? '#DCFCE7' : PRIMARY_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Icon sx={{ fontSize: 20, color: s.done ? '#16A34A' : PRIMARY }} />
                                    </Box>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Stack direction="row" spacing={0.7} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                                            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#0F172A' }}>{meta.label || s.title}</Typography>
                                            {!s.required && <Chip label="Optional" size="small" sx={{ height: 17, fontSize: 9, fontWeight: 700, bgcolor: '#F1F5F9', color: '#64748B' }} />}
                                        </Stack>
                                        <Typography sx={{ fontSize: 11.5, color: '#94A3B8', lineHeight: 1.35 }}>{s.detail || s.description}</Typography>
                                    </Box>
                                    {s.done ? (
                                        <Chip label="Done" size="small" sx={{ flexShrink: 0, height: 24, fontSize: 10.5, fontWeight: 800, bgcolor: '#DCFCE7', color: '#16A34A' }} />
                                    ) : meta.to ? (
                                        <Tooltip arrow title={`Go to ${meta.label || s.title}`}>
                                            <IconButton onClick={() => go(meta.to)} size="small" sx={{ flexShrink: 0, bgcolor: PRIMARY_LIGHT, color: PRIMARY, borderRadius: '9px', width: 34, height: 34, '&:hover': { bgcolor: '#E7DFFC' } }}>
                                                <ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Tooltip>
                                    ) : null}
                                </Stack>
                            );
                        })}
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 2, py: 1.8, borderTop: '1px solid #F1F3F7', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                        onClick={load}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={14} /> : <RefreshRoundedIcon sx={{ fontSize: 17 }} />}
                        sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12.5, color: '#475569', bgcolor: '#F1F5F9', borderRadius: '8px', height: 40, px: 2, '&:hover': { bgcolor: '#E2E8F0' } }}
                    >
                        Re-check
                    </Button>
                    <Box sx={{ flex: 1 }} />
                    {isMasterAdmin ? (
                        <Button onClick={() => setOpen(false)} sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12.5, color: '#64748B', borderRadius: '8px', height: 40, px: 2 }}>
                            Skip for now
                        </Button>
                    ) : (
                        <Typography sx={{ fontSize: 11.5, color: '#94A3B8', alignSelf: 'center', mr: 0.5 }}>
                            Complete the required steps to start using {entityName}.
                        </Typography>
                    )}
                    <Button
                        onClick={() => setOpen(false)}
                        sx={{ background: GRADIENT, color: '#fff', textTransform: 'none', fontWeight: 800, fontSize: 12.5, borderRadius: '8px', height: 40, px: 2.4, boxShadow: 'none', '&:hover': { filter: 'brightness(0.96)' } }}
                    >
                        Got it
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
